// Two decisions per branch: where its VERTICES come from, and what makes its decoders EXPRESS.
// Notes: ~/comments/aos-threejs-poc/faceDrivePlan_resolveFaceDrivePlan_two_axes.md

import { branchExpression, type BranchExpression } from "../assets/sceneManifest.ts";
import type { BranchSpec, SceneManifest } from "../assets/sceneManifest.ts";
import type { ResolvedRig } from "../inference/rigMode.ts";
import type { FaceRigMode, RenderPlan } from "./renderPlan.ts";

/** Coarse source, all a MANIFEST can answer. `orl` and `part-skin` refine at build/drive time. */
export type PlannedSource = "rig2mesh" | "orl" | "orl-joints" | "part-skin" | "static";

/** What refines a coarse source once the mesh or the part rig is in hand. */
export type SourceRefinement = "orl-skin-or-shell" | "rigid-head" | null;

/** Re-exported, not re-declared: the derivation is shared with the legacy path
 *  (sceneManifest.branchExpression) so the two cannot drift. */
export type Expression = BranchExpression;

export interface BranchDrivePlan {
  region: string;
  source: PlannedSource;
  /** `orl` splits by vertex count at build; `part-skin` falls to the head bone with no part. */
  refinement: SourceRefinement;
  expression: Expression;
  why: string;
}

export interface DriveRefusal {
  region: string;
  field: "source" | "expression";
  requested: string;
  resolved: string;
  why: string;
}

export interface FaceDrivePlan {
  rig: ResolvedRig;
  /** The part rig poses the facial joints (SyntheticOGSFace). */
  synthetic: { enabled: boolean; source: "bundle" | "blueprint" | "default" };
  branches: BranchDrivePlan[];
  /** The gaze-conditioned eye branch renders its own SplatMesh; it is not a mesh branch. */
  eyes: { region: string; lookAtCamera: boolean } | null;
  /** Every override the bundle could not honour, named. Empty on a clean plan. */
  refusals: DriveRefusal[];
}

/** What the BUNDLE alone says a branch's vertices are. */
function bundleSource(branch: BranchSpec, live: boolean, rig: ResolvedRig, synthetic: boolean): PlannedSource {
  // A fused geom takes rig_params straight in: the decoders animate, the mesh never moves.
  if (branch.fused) return "static";
  // A local_disp head's expression IS its vertex displacement, so something must pose it.
  if (branch.headCond) return synthetic ? "orl-joints" : "part-skin";
  if (!live) return "part-skin";
  if (rig === "orl") return "orl";
  if (rig === "rig2mesh") return branch.rig2mesh ? "rig2mesh" : "static";
  return "static";
}

function refinementFor(source: PlannedSource): SourceRefinement {
  if (source === "orl") return "orl-skin-or-shell";
  if (source === "part-skin") return "rigid-head";
  return null;
}

/** Can this bundle produce `want` for this branch? The reason, or null when it can. */
function sourceRefusal(want: PlannedSource, branch: BranchSpec, rig: ResolvedRig, synthetic: boolean): string | null {
  if (want === "rig2mesh" && !branch.rig2mesh) return "this branch declares no rig2mesh";
  if (want === "orl" && rig !== "orl") return `the resolved rig is '${rig}', not the exact MetaHuman rig`;
  if (want === "orl-joints" && !synthetic) return "the synthetic face is off, so nothing poses the facial joints";
  if (want === "orl-joints" && !branch.headCond) return "only a local_disp head takes its expression from posed vertices";
  return null;
}

function expressionRefusal(want: Expression, branch: BranchSpec, live: boolean): string | null {
  if (want === "fused" && !branch.fused) return "this branch declares no fused geom";
  if (want === "local-disp" && !branch.headCond) return "this branch declares no head_cond displacement field";
  if (want === "trunk" && !branch.trunk) return "this branch declares no trunk";
  if (want === "trunk" && !live) return "this branch consumes no live control vector";
  return null;
}

const SOURCE_FOR_MODE: Partial<Record<FaceRigMode, PlannedSource>> = {
  rig2mesh: "rig2mesh", "orl-skin": "orl", "orl-joints": "orl-joints", off: "static",
};

export interface FaceDrivePlanInput {
  manifest: SceneManifest;
  rig: ResolvedRig;
  /** The branches that consume the live control vector (resolvePipelineKind). */
  liveBranches: readonly string[];
  /** The blueprint's request. Omit for the bundle's own choices throughout. */
  graph?: RenderPlan | null;
}

/** Resolve both axes for every branch, honouring the graph where the bundle can. */
export function resolveFaceDrivePlan({ manifest, rig, liveBranches, graph = null }: FaceDrivePlanInput): FaceDrivePlan {
  const live = new Set(liveBranches);
  const request = graph?.faceDrive ?? null;
  const refusals: DriveRefusal[] = [];

  // Precedence: the blueprint, then the bundle, then off.
  const synthetic = request && request.syntheticFace !== "auto"
    ? { enabled: request.syntheticFace === "on", source: "blueprint" as const }
    : manifest.syntheticFace
      ? { enabled: true, source: "bundle" as const }
      : { enabled: false, source: "default" as const };

  // The head is the only branch the face-drive node speaks for; a part node speaks per region.
  const headRegion = manifest.branches.head ? "head" : manifest.order[0];
  const branches: BranchDrivePlan[] = [];
  let eyes: FaceDrivePlan["eyes"] = null;

  for (const region of manifest.order) {
    const branch = manifest.branches[region];
    if (!branch) continue;
    if (branch.gazeConditioned) {
      eyes = { region, lookAtCamera: graph?.parts[region]?.lookAtCamera ?? true };
      continue;
    }
    const isLive = live.has(region);
    let source = bundleSource(branch, isLive, rig, synthetic.enabled);
    let expression = branchExpression(branch, isLive);
    let why = "declared by this bundle";

    const wantSource = region === headRegion && request && request.mode !== "auto"
      ? SOURCE_FOR_MODE[request.mode] ?? null
      : null;
    const partSource = graph?.parts[region]?.vertexSource;
    const requestedSource = (partSource && partSource !== "auto" ? partSource : null) ?? wantSource;
    if (requestedSource && requestedSource !== source) {
      const refusal = sourceRefusal(requestedSource, branch, rig, synthetic.enabled);
      if (refusal) refusals.push({ region, field: "source", requested: requestedSource, resolved: source, why: refusal });
      else { source = requestedSource; why = "asked for by the blueprint"; }
    }

    const wantExpression = region === headRegion && request && request.expression !== "auto"
      ? request.expression : null;
    const partDecode = graph?.parts[region]?.decode;
    const requestedExpression: Expression | null = partDecode === "frozen" ? "frozen" : (wantExpression ?? null);
    if (requestedExpression && requestedExpression !== expression) {
      // 'frozen' is always producible — it is "stop re-decoding", not a decoder the bundle must have.
      const refusal = requestedExpression === "frozen" ? null : expressionRefusal(requestedExpression, branch, isLive);
      if (refusal) refusals.push({ region, field: "expression", requested: requestedExpression, resolved: expression, why: refusal });
      else { expression = requestedExpression; why = "asked for by the blueprint"; }
    }
    // Asking a frozen branch to decode per frame needs a decoder it may not have.
    if (partDecode === "per-frame" && expression === "frozen") {
      refusals.push({ region, field: "expression", requested: "per-frame", resolved: "frozen",
        why: "this branch has no per-frame expression — it decodes once and re-lifts" });
    }

    branches.push({ region, source, refinement: refinementFor(source), expression, why });
  }

  return { rig, synthetic, branches, eyes, refusals };
}

/** One line per branch, for the boot log and the composite card. */
export function describeFaceDrivePlan(plan: FaceDrivePlan): string[] {
  const lines = plan.branches.map((b) => `${b.region}: ${b.source}${b.refinement ? ` (${b.refinement})` : ""} / ${b.expression} — ${b.why}`);
  if (plan.eyes) lines.push(`${plan.eyes.region}: gaze-conditioned, own SplatMesh (eye contact ${plan.eyes.lookAtCamera ? "on" : "off"})`);
  for (const r of plan.refusals) lines.push(`${r.region}: REFUSED ${r.field} '${r.requested}' — ${r.why}; staying on '${r.resolved}'`);
  return lines;
}
